import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { CiUser } from "react-icons/ci";
import { HiMenuAlt2 } from "react-icons/hi";
import adminService from "../api/services/adminService";
import toast from "react-hot-toast";
import { clearAdminSessionAndRedirect } from "../utils/adminSession";
import {
  adminRoleBadgeClass,
  formatAdminRoleLabel,
  getAdminPanelRoleDisplay,
} from "../utils/adminPanelRole";
import DailyTargetsDropdown from "../components/Topbar/DailyTargetsDropdown";

function readStoredUser() {
  try {
    const raw = localStorage.getItem("adminUserDetails");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function formatCallPreview(title) {
  const t = String(title || "").trim();
  if (!t) return "Naseebi Agent";
  if (t.toLowerCase().endsWith("naseebi agent")) return t;
  return `${t} Naseebi Agent`;
}

const Topbar = ({ showNav, setShowNav }) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [userData, setUserData] = useState(() => readStoredUser());
  const [agentTitle, setAgentTitle] = useState(
    () => readStoredUser()?.agent_title || ""
  );
  const [savingTitle, setSavingTitle] = useState(false);
  const userRole = getAdminPanelRoleDisplay();
  const roleLabel = formatAdminRoleLabel(userRole);
  const roleBadgeClass = adminRoleBadgeClass(userRole);

  useEffect(() => {
    setAgentTitle(userData?.agent_title || "");
  }, [userData?.agent_title]);

  const displayName =
    userData?.firstName === "Naseebi"
      ? userData?.lastName
      : userData?.firstName || "Admin";

  const callPreview = useMemo(() => formatCallPreview(agentTitle), [agentTitle]);

  const logoutHandler = async () => {
    try {
      await adminService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      toast.success("Logged out successfully");
      clearAdminSessionAndRedirect(null);
    }
  };

  const saveAgentTitle = async () => {
    try {
      setSavingTitle(true);
      const res = await adminService.updateMyAgentTitle(agentTitle);
      const nextTitle = res?.agent_title || "";
      const nextUser = {
        ...(userData || {}),
        agent_title: nextTitle,
      };
      localStorage.setItem("adminUserDetails", JSON.stringify(nextUser));
      setUserData(nextUser);
      setAgentTitle(nextTitle);
      toast.success(
        res?.callDisplayName
          ? `Call name: ${res.callDisplayName}`
          : "Call title saved"
      );
    } catch (error) {
      toast.error(error?.message || "Failed to save call title");
    } finally {
      setSavingTitle(false);
    }
  };

  return (
    <div
      className={`w-screen h-16 md:h-20 flex justify-between items-center transition-all duration-[400ms] px-2 md:px-4`}>
      <button
        type="button"
        aria-label="Toggle navigation"
        className="md:hidden flex items-center justify-center p-2 rounded hover:bg-gray-100 text-gray-700"
        onClick={() => setShowNav(!showNav)}>
        <HiMenuAlt2 className="h-7 w-7" />
      </button>
      <div className="text-black flex flex-row font-bold items-center justify-center py-2 md:py-3 px-2 md:px-3 lg:ml-6 ml-1 min-w-0">
        <div className="mr-2">
          <img
            src={require("../assests/logo.png")}
            alt="Naseebi Admin logo"
            className="h-6 md:h-8 w-auto"
          />
        </div>
        <div className="min-w-0">
          <p className="text-sm md:text-xl text-[#FF2D38] font-bold truncate">
            Naseebi Admin Panel
          </p>
        </div>
      </div>
      <div
        className={`transition-all flex gap-2 md:gap-3 items-center duration-[400ms] shrink-0 ml-auto ${
          showNav ? "" : "pr-6  "
        }`}>
        <DailyTargetsDropdown />
        <div className="font-bold hidden sm:block text-right leading-tight">
          <p className="text-sm md:text-base text-gray-900">{displayName}</p>
          <span
            className={`inline-flex mt-0.5 text-[10px] md:text-[11px] font-semibold px-2 py-0.5 rounded-full border ${roleBadgeClass}`}
          >
            {roleLabel}
          </span>
        </div>
        <div>
          <Popover className="relative">
            <PopoverButton
              onClick={() => setOpenMenu(!openMenu)}
              className="flex gap-2 outline-none mr-1 md:mr-8 cursor-pointer text-gray-700">
              {userData?.imageUrl ? (
                <div className="relative w-10 h-10 md:h-12 md:w-12 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={userData.imageUrl}
                    alt="Your avatar"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <CiUser className="h-8 w-8 md:h-10 md:w-10 border-2 rounded-full border-gray-600 p-2 mt-[6px] hover:bg-black hover:text-white hover:font-extrabold hover:border-white" />
              )}
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform scale-95"
              enterTo="transform scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform scale-100"
              leaveTo="transform scale-95">
              <PopoverPanel className="mr-4 md:mr-10 absolute -right-2 sm:-right-8 z-50 mt-3 bg-white border-2 border-[#FEBE77] shadow-gray-400 shadow-lg rounded-md w-[260px] sm:w-[280px]">
                <div className="pt-4 px-3 pb-3">
                  <p className="text-sm font-semibold text-gray-900 truncate text-center">
                    {displayName}
                  </p>
                  <div className="flex justify-center">
                    <span
                      className={`inline-flex mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${roleBadgeClass}`}
                    >
                      {roleLabel}
                    </span>
                  </div>
                  {userData?.email ? (
                    <p className="text-[11px] text-gray-500 mt-2 truncate text-center">
                      {userData.email}
                    </p>
                  ) : null}

                  <div className="mt-3 text-left border-t border-gray-100 pt-3">
                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                      Call title (sudo name)
                    </label>
                    <input
                      type="text"
                      value={agentTitle}
                      maxLength={80}
                      onChange={(e) => setAgentTitle(e.target.value)}
                      placeholder="e.g. Ayesha"
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF2D38]"
                    />
                    <p className="mt-1 text-[10px] text-gray-500 leading-snug">
                      Customers see:{" "}
                      <span className="font-semibold text-gray-700">{callPreview}</span>
                    </p>
                    <button
                      type="button"
                      disabled={savingTitle}
                      onClick={saveAgentTitle}
                      className="mt-2 w-full rounded bg-[#FF2D38] text-white text-[0.75rem] font-semibold py-1.5 hover:bg-[#e02630] disabled:opacity-60"
                    >
                      {savingTitle ? "Saving…" : "Save call title"}
                    </button>
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={logoutHandler}
                      className="mt-3 text-center justify-center w-[60%] sm:w-[50%] rounded bg-red-600 text-white font-[600] py-1 mb-1 shadow-md text-[0.8rem] hover:bg-red-700 hover:text-white">
                      Logout
                    </button>
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default Topbar;
